import { UseMutationOptions, useQueryClient } from "@tanstack/react-query";

import { graphql } from "lib/generated/app-service-gql";
import {
  CreateCustomerDocumentsMutation,
  CreateCustomerDocumentsMutationVariables,
} from "lib/generated/app-service-gql/graphql";
import { UpdateFileStatus } from "../../../../components/content_library/DocumentUploadProgressProvider";
import { CustomerDocumentStatus } from "../../../types/graphQLEnums";
import { useGraphQlMutation } from "../useGraphQlClient";
import { customerDocumentsKey } from "./useCustomerDocuments";

const CREATE_CUSTOMER_DOCUMENTS_QUERY = graphql(`
  mutation createCustomerDocuments($request: CreateCustomerDocumentsRequest!) {
    createCustomerDocuments(CreateCustomerDocumentsRequest: $request) {
      documents {
        id
        resourceId
        fileName
        signedUrl
        isFolder
        documentType
        uiPath
        status
      }
    }
  }
`);

/**
 * Gets the s3 bucket URLs and inserts customer documents in a postgres table
 * @param options
 * @returns
 */
export function useCreateCustomerDocuments(
  updateFileStatus?: UpdateFileStatus,
  options?: UseMutationOptions<
    CreateCustomerDocumentsMutation,
    unknown,
    CreateCustomerDocumentsMutationVariables,
    unknown
  >,
) {
  const client = useQueryClient();

  const { data, ...rest } = useGraphQlMutation(CREATE_CUSTOMER_DOCUMENTS_QUERY, {
    ...options,
    async onSuccess(data, variables, context) {
      const createdDocs = data.createCustomerDocuments.documents;

      for (const doc of createdDocs) {
        if (doc.isFolder) {
          const { documentType, resourceId, uiPath } = doc;
          // If the document is a folder, we don't need to update file status
          // - as folders aren't uploaded per say, but we can update the cache
          await client.invalidateQueries({
            queryKey: customerDocumentsKey({ documentType, resourceId, uiPath }),
            exact: false, // Allow matching without pagination;
          });
        } else {
          if (!updateFileStatus) {
            continue;
          } else if (doc.status == CustomerDocumentStatus.Success) {
            // This is a duplicate upload, set to complete
            updateFileStatus({
              fileName: doc.fileName,
              documentId: doc.id,
              updates: {
                isUploaded: true,
                documentId: doc.id,
                isProcessed: true,
                isFinished: true,
                isError: false,
              },
            });
          } else {
            // If the document is not a folder, we need to add the document ID.
            updateFileStatus({
              fileName: doc.fileName,
              documentId: doc.id,
              updates: {
                isUploaded: false,
                documentId: doc.id,
                isProcessed: false,
                isError: false,
              },
            });
          }
        }
      }
      options?.onSuccess?.(data, variables, context);
    },
    // If there's an error upon upload & creation of a document, we must update the file status
    onError(err, vars, context) {
      if (!updateFileStatus) {
        return;
      }
      const filesToUpdate = vars.request.newDocuments.filter((f) => f.fileName);
      for (const file of filesToUpdate) {
        // Update the file status to indicate an error
        updateFileStatus({
          fileName: file.fileName,
          documentId: undefined,
          updates: { isError: true, isFinished: true, isProcessed: false },
        });
      }
      options?.onError?.(err, vars, context);
    },
  });

  return { data: data?.createCustomerDocuments, ...rest };
}
